<template>
	<div class="comments">
		<ol ref="comments" id="comments">
			<template v-for="(entity, index) in commentGroupsSorted">
				<comment-expander
					v-if="entity.type === 'range'"
					:key="`${entity.end}${entity.start}`"
					:range="entity.data"
					:index="index"
					:isExpanding="expandAllLoading"
					@comments:expand="expandRange" 
				/>

				<template v-if="entity.type === 'comment'">
					<template v-if="entity.data.comment_type === 'comment' || entity.data.comment_type === 'note'">
						<comment
							ref="itemRefs"
							:key="entity.data.id"
							:comment="entity.data"
							@comment:removed="handleCommentRemoved" 
							@comment:toggle-collapsed="handleCommentToggle"
						/>
					</template>

					<template v-else>
						<event :key="entity.id" :event="entity.data" />
					</template>
				</template>
			</template>
		</ol>

		<textarea style="width: 100px; height: 100px; position: absolute; top: 0; left: -200px;" ref="dummyTextareaForCopyOperations"></textarea>

		<comments-notification
			:unreadMessages="unreadMessages"
			@loadNewMessages="loadNewMessages"
		/>
	</div>
</template>

<script>
import { ref } from 'vue'
import axios from 'axios';
import dayjs from 'dayjs';
import Comment from '../components/Comment.vue';
import CommentExpander from '../components/CommentExpander.vue';
import CommentsNotification from '../components/CommentsNotification.vue';
import Note from '../components/Note.vue';
import Event from '../components/Event.vue';
import Loading from '../components/Loading.vue';

export default {
	name: 'app-comments',

	components: {
		Comment,
		CommentExpander,
		CommentsNotification,
		Note,
		Event,
		Loading,
	},

	data() {
		return {
			commentGroups: window._commentGroupsData,
			showNotifications: true,
			unreadMessages: [],
			expandAllLoading: false
		};
	},

	computed: {
		commentGroupsSorted() {
			const sortedGroups = this.commentGroups.sort((a, b) => {
				const aValue = a.data.id ? Number(a.data.id) : Number(a.data.end);
				const bValue = b.data.id ? Number(b.data.id) : Number(b.data.end);

				return aValue - bValue;
			});

			const uniqueGroups = [];
			for (const group of sortedGroups) {
				const groupValue = group.data.id || group.data.end;
				const lastGroup = uniqueGroups[uniqueGroups.length - 1];

				if (!lastGroup || lastGroup.data.id !== groupValue) {
					uniqueGroups.push(group);
				}
			}
			return uniqueGroups;
		},

		commentGroupsCache() {
			return window._commentGroupsDataCache;
		},
	},

	methods: {
		bindHandlers() {
			window.$eventDispatcher.$on('comments:new', this.notifyUnread);
			window.$eventDispatcher.$on('comments:added', this.addComment);
			window.$eventDispatcher.$on('comments:expandAll', this.expandAll);
			window.$eventDispatcher.$on('comments:collapseAll', this.collapseAll);
		},

		handleCommentToggle({commentId, isCollapsed}) {
			this.commentGroups = this.commentGroups.map(comment => {
				if(comment.data?.id === commentId) {
					comment.data.is_expanded = isCollapsed;
				}

				return comment;
			});
		},

		getHighlightedCommentId() {
			const hash = window.location.hash;

			if (!hash) {
				return false;
			}

			const matches = hash.match(/(\#comment\-)(\d+)/);

			return matches && parseInt(hash.match(/(\#comment\-)(\d+)/)[2]);
		},

		concatCommentGroups(groups, map) {
			return new Promise((resolve, reject) => {
				// Don't spit all comments at once; instead add them as chunks.
				// he perceived performance is way better with this approach
				let remaining = [];
				const chunkSize = 100;
				if (groups.length >= chunkSize) {
					remaining = groups.slice(chunkSize);
					groups = groups.slice(0, chunkSize);
				}

				// Map groups if mapping function is provided
				if (typeof map === 'function') {
					groups = groups.map(map);
				}
				this.commentGroups = this.commentGroups.concat(groups);

				if (remaining.length) {
					this.$nextTick(() => {
						setTimeout(() => {
							this.concatCommentGroups(remaining, map).then(() => resolve());
						}, 0)
					});
				} else {
					resolve();
				}
			})
		},

		loadCommentsRange(startId, endId, highlightId, cb, map) {
			const callback = () => {
				if (typeof cb === 'function') {
					cb();
				}
			};

			if (startId === Infinity || endId === -Infinity) {
				callback();

				return;
			}

			if (this.commentGroupsCache) {
				const inCache =
					this.commentGroupsCache.findIndex(
						commentGroup => parseInt(commentGroup.data.id) === parseInt(startId)
					) >= 0 &&
					this.commentGroupsCache.findIndex(
						commentGroup => parseInt(commentGroup.data.id) === parseInt(endId)
					) >= 0;

				if (inCache) {
					const commentsRange = this.commentGroupsCache.filter(
						commentGroup => (
							parseInt(commentGroup.data.id) >= parseInt(startId)
							&& parseInt(commentGroup.data.id) <= parseInt(endId)
						)
					);

					this.concatCommentGroups(commentsRange, map).then(() => {
						callback();
					});

					return;
				}
			}

			const url = `${window.location.pathname}/comment-range`;
			const params = {
				from: startId,
				to: endId,
			}

			if (highlightId) {
				params.highlight = highlightId;
			}

			return axios
				.get(url, { params })
				.then(res => {
					if (res.data) {
						this.concatCommentGroups(res.data.groups, map).then(() => {
							callback();
						});
					} else {
						callback();
					}
				})
				.catch(err => {
					console.log(err);
				});
		},

		expandRange({payload, hidden, range}) {
			return this.loadCommentsRange(payload.from, payload.to, null, () => {
				this.commentGroups = this.commentGroups
				.filter(commentGroup => !(
					commentGroup.type === 'range' 
					&& hidden?.length === 0 
					&& commentGroup.data.start === range.start 
					&& commentGroup.data.end === range.end
				))
				.map(commentGroup => {
					if(commentGroup.type === 'range' && commentGroup.data.start === range.start && commentGroup.data.end === range.end) {
						commentGroup.data.hidden = hidden;
						commentGroup.data.start = hidden[0] || range.start;
						commentGroup.data.end = hidden[hidden.length - 1] || range.end;
					}
					return commentGroup;
				});
			});
		},

		expandAll() {
			const activeRanges = this.commentGroups.filter(
				commentGroup => commentGroup.type === 'range'
			);

			const starts = activeRanges.map(range => range.data.start);
			const ends = activeRanges.map(range => range.data.end);

			const minStart = Math.min(...starts);
			const maxEnd = Math.max(...ends);

			this.expandAllLoading = true;

			this.loadCommentsRange(
				minStart,
				maxEnd,
				null,
				() => {
					this.expandAllLoading = false;
					this.commentGroups = this.commentGroups.filter(
						commentGroup => commentGroup.type !== "range"
					);
					this.commentGroups.forEach((commentGroup, index) => {
						commentGroup.data.is_expanded = true;
					});

					window.$eventDispatcher.$emit("comments:expandedAll");
				},
				(commentGroup) => {
					commentGroup.data.is_expanded = true;
					return commentGroup;
				}
			);
		},

		collapseAll() {
			if (this.expandAllLoading) {
				return;
			}

			this.commentGroups.forEach((commentGroup, index) => {
				commentGroup.data.is_expanded = false;
			});

			window.$eventDispatcher.$emit('comments:collapsedAll');
		},

		addComment(id) {
			return this.loadCommentsRange(
				id,
				id,
				null,
				null,
				(commentGroup) => {
					commentGroup.data.is_expanded = true;
					commentGroup.data.slideReveal = true;

					return commentGroup;
				}
			);
		},

		notifyUnread(ids) {
			if (!this.showNotifications) {
				return;
			}

			this.unreadMessages = ids;
		},

		loadNewMessages() {
			const start = Math.min(...this.unreadMessages);
			const end = Math.max(...this.unreadMessages);

			return this.loadCommentsRange(
				start,
				end,
				null,
				() => {
					this.unreadMessages = [];

					if (window.msgMonitor) {
						const lastCommentId =
							parseInt(end) > parseInt(window.msgMonitor.lastCommentId)
								? end
								: window.msgMonitor.lastCommentId;

						window.msgMonitor.lastCommentId = lastCommentId;
					}

				},
				(commentGroup) => {
					commentGroup.data.is_expanded = true;
					commentGroup.data.slideReveal = true;

					return commentGroup;
				}
			);
		},

		focusComment(id) {
			const comment = this.commentGroups.find(c => c.type === "comment" && parseInt(c.data.id) === parseInt(id));
			comment.data.is_expanded = true;

			const $el = $(`#comment-${id}`);
			$('html, body').animate({ scrollTop: $el.offset().top }, 400)
				// Use a jQuery promise since callbacks would be called twice
				// (once for the `html` and second time for `body`) causing
				// problems with CSS transitions
				.promise()
				.then(() => {
					$el.removeClass('comment-lowlight').addClass('comment-hightlight');
					setTimeout(() => {
						$el.toggleClass('comment-hightlight comment-lowlight');
					}, 400);
				})
		},


		loadHighlightedComment(id) {
			const commentRange = this.commentGroups.find(
				commentGroup => (
					commentGroup.data.start <= id
					&& commentGroup.data.end >= id
				)
			);

			// The comment is not in the thread. Maybe we need some warning for the end user here?
			if (!commentRange) {
				return null;
			}

			return this.loadCommentsRange(
				commentRange.data.start,
				commentRange.data.end,
				id,
				() => {
					this.commentGroups = this.commentGroups.filter(
						commentGroup => commentGroup !== commentRange
					);
					this.$nextTick(() => this.focusComment(id));
				}
			);
		},

		highlightComment() {
			const highlightedCommentId = this.getHighlightedCommentId();
			if (highlightedCommentId) {
				const isLoaded = this.commentGroups.findIndex(
					comment => parseInt(comment.data.id) === highlightedCommentId
				) >= 0;

				if (!isLoaded) {
					this.loadHighlightedComment(highlightedCommentId);
				} else {
					this.focusComment(highlightedCommentId);
				}
			}
		},

		getCommentComponentByCommentId(commentId) {
			return this.itemRefs.find(item => item.comment && parseInt(item.comment.id) === parseInt(commentId))
		},

		/**
		 * Warning: fragile code ahead!
		 *
		 * You can copy from the document only in an event handler of trusted event(i.e. one that
		 * originates from the user). Additionally, copying from a textarea that's not visible
		 * doesn't work, so the textarea needs to be pushed away from the viewport with position.
		 */
		setupClipboard() {
			var selectionInComment = false;
			var $lastCopyTarget;

			var $hiddenSelectionInput = $(this.$refs.dummyTextareaForCopyOperations);
			const vm = this;

			$(document).bind('mousedown mouseup keydown', function(event) {

				var $target = $(event.target);

				// if(event.type === 'keydown' && selectionInComment && event.keyCode === 67 && event.altKey && event.shiftKey) {
				var isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
				var ctrlOrCmd = (isMac && event.metaKey) || (event.ctrlKey);

				if(event.type === 'keydown' && selectionInComment && ctrlOrCmd && event.keyCode === 67 && (event.shiftKey || event.altKey)) {
					event.stopPropagation();
					event.preventDefault();

					try {
						$hiddenSelectionInput.select();

						document.execCommand('copy');

						vm.getCommentComponentByCommentId($lastCopyTarget.data('commentid')).flashAfterCopy();

					} catch(errors) {
						console.log(errors);
					}

					selectionInComment = false;
					return;
				}

				if(event.type === 'mousedown' || event.type === 'mouseup') {
					selectionInComment = $target?.hasClass('comment-entry') || $target?.parents('.comment-entry').length;
					if (
						event.type === 'mouseup' &&
						!selectionInComment &&
						$lastCopyTarget?.hasClass('comment') &&
						!$lastCopyTarget?.hasClass('event')
					) {
						selectionInComment = true;
					} else {
						$lastCopyTarget = $target.parents('.comment:eq(0)');
					}
				}

				if(selectionInComment && (event.type === 'mouseup' || event.type === 'keydown')) {
					const commentComponent = vm.getCommentComponentByCommentId($lastCopyTarget.data('commentid'));
					if (commentComponent && commentComponent.getSelectedTextAsMarkdown) {
						$hiddenSelectionInput.val(commentComponent.getSelectedTextAsMarkdown() || commentComponent.getCommentAsMarkdown());
					}
				}
			});
		},

		bindCommentReminderHandler() {
			$(this.$refs.comments).on('click', '.comment-reminder', (event) => {
				event.preventDefault();
				event.stopPropagation();

				const target = event.target;
				const date = new Date(parseInt($(target).data('time') || 0));

				// Normalize Date
				if (date.getHours() === 0 && date.getMinutes() === 0) {
					date.addHours(9);
				}

				const dateFormated = dayjs(date).format('YYYY-MM-DD HH:mm:ss');
				const $projectRemindersForm = $('#project-reminders-form');
				const $sidebarTrigger = $('#sidebar-trigger');
				const $reminderBtn = $('.show-hidden-form[data-holder="add_reminder"]');
				const $reminderWhen = $('#reminder_when');
				const $whenCol = $projectRemindersForm.find('.when');
				const $whenStaticCol = $projectRemindersForm.find('.when-static');

				if ($sidebarTrigger.length) {
					$sidebarTrigger.trigger('click');
				}

				if ($projectRemindersForm) {
					$projectRemindersForm.addClass('is-sticked');
				}

				if ($reminderBtn.length && $reminderWhen.length) {
					$reminderBtn.trigger('click');

					$reminderWhen.val(date.toString());
					$reminderWhen.trigger('keyup');

					$whenStaticCol.find('span').text(dateFormated);

					$whenCol.hide();
					$whenStaticCol.show();
				}
			});
		},

		/**
		 * Handle removed comment event.
		 * @param  {String} commentId
		 * @return {void}
		 */
		handleCommentRemoved(commentId) {
			this.commentGroups = this.commentGroups.filter(comment => comment.data.id !== commentId);
		},
	},

	setup() {
		const itemRefs = ref([])

		return {
			itemRefs
		}
	},

	mounted() {
		this.bindHandlers();
		this.highlightComment();

		$(window).on("hashchange", e => {
			this.highlightComment()
		});

		this.setupClipboard();

		this.bindCommentReminderHandler();
	},
}
</script>
